type RemoveChild = <T extends Node>(this: Node, child: T) => T
type InsertBefore = <T extends Node>(this: Node, node: T, referenceNode: Node | null) => T

/**
 * Wraps a native `removeChild` so that removing a node whose `parentNode` is no
 * longer this element becomes a safe no-op instead of throwing `NotFoundError`.
 *
 * Mobile browser translation (Chrome on Android/iOS, Mobile Safari, the Google
 * app) and similar tools rewrap text nodes underneath React, so by the time
 * React commits a deletion the node it tracked may no longer be a direct child.
 * See facebook/react#11538.
 */
export function guardedRemoveChild(original: RemoveChild): RemoveChild {
    return function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            return child
        }

        return original.call(this, child) as T
    }
}

/**
 * Wraps a native `insertBefore` so that inserting before a reference node that is
 * no longer a child of this element appends the node instead of throwing
 * `NotFoundError`, mirroring {@link guardedRemoveChild}.
 */
export function guardedInsertBefore(original: InsertBefore): InsertBefore {
    return function <T extends Node>(this: Node, node: T, referenceNode: Node | null): T {
        if (referenceNode !== null && referenceNode.parentNode !== this) {
            return original.call(this, node, null) as T
        }

        return original.call(this, node, referenceNode) as T
    }
}

let guardInstalled = false

/**
 * Installs the DOM translation guards on `Node.prototype`. Idempotent and safe to
 * call in environments without a DOM. Call once, before React renders.
 */
export function installDomTranslateGuard(): void {
    if (guardInstalled) {
        return
    }

    if (typeof Node === 'undefined' || typeof Node.prototype === 'undefined') {
        return
    }

    guardInstalled = true

    Node.prototype.removeChild = guardedRemoveChild(Node.prototype.removeChild)
    Node.prototype.insertBefore = guardedInsertBefore(Node.prototype.insertBefore)
}
